/**
 * CA Bundle Entry Row
 *
 * Certificate-list editor for `pem-ca-bundle` runtime-config slots. Same
 * ownership contract as RuntimeConfigEntryRow: `editable` comes from the
 * dialog; when it is false, Add/Remove call `onRequestEdit` (fork confirm)
 * and the admin repeats the action. Every change is a whole-bundle `onSave`,
 * so fork-on-edit, Revert and the source banner behave as for any file value.
 */

import { useState, type ReactElement } from 'react';
import type { RuntimeConfigCtx } from '../../api/adminApi';
import type { RuntimeConfigEntry } from '../../schemas/admin.schemas';
import { removePemBlock } from '../../utils/caBundle';
import { CaCertificateTable } from './CaCertificateTable';
import { CaUploadForm } from './CaUploadForm';

const SEED_ACTOR = 'sanctum:site-trust-seed';

interface CaBundleEntryRowProps {
  featureId: string;
  ctx?: RuntimeConfigCtx;
  entry: RuntimeConfigEntry;
  onSave: (value: string) => Promise<void>;
  onDelete: () => void;
  onRequestEdit: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  editable: boolean;
  canDelete: boolean;
}

export function CaBundleEntryRow({
  featureId, ctx, entry, onSave, onDelete, onRequestEdit, isSaving, isDeleting, editable, canDelete,
}: CaBundleEntryRowProps): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const certificates = entry.certificates ?? [];
  const value = entry.value ?? '';

  const handleRemove = (index: number): void => {
    if (!editable) { onRequestEdit(); return; }
    if (certificates.length === 1) {
      // An empty CA bundle is refused server-side; removing the last cert is a delete.
      if (!canDelete) {
        setError('Remove all certificates with Delete or Revert.');
        return;
      }
      setError(null);
      onDelete();
      return;
    }
    const next = removePemBlock(value, index, certificates.length);
    if (next === null) {
      setError('The stored bundle does not match the certificate list. Reload and try again.');
      return;
    }
    setError(null);
    void onSave(next).catch(() => {});
  };

  return (
    <li className="runtime-config__row">
      <div className="runtime-config__row-header">
        <div className="runtime-config__identity">
          <span className="runtime-config__name">
            {entry.label || entry.name}
            <span className="runtime-config__subtext"> ({entry.name})</span>
          </span>
          {entry.description && <span className="runtime-config__description">{entry.description}</span>}
        </div>
        {entry.updated_by === SEED_ACTOR && (
          <span className="runtime-config__badge runtime-config__badge--global">Seeded from site</span>
        )}
      </div>
      {entry.is_set && entry.updated_at && (
        <div className="runtime-config__meta">
          <span>
            Updated {new Date(entry.updated_at).toLocaleString()}
            {entry.updated_by ? ` by ${entry.updated_by}` : ''}
          </span>
        </div>
      )}
      <CaCertificateTable certificates={certificates} onRemove={handleRemove} disabled={isSaving} />
      {error && <div className="dialog__error" role="alert">{error}</div>}
      <CaUploadForm
        featureId={featureId}
        name={entry.name}
        ctx={ctx}
        existing={value}
        editable={editable}
        onRequestEdit={onRequestEdit}
        onMerged={onSave}
        disabled={isSaving}
      />
      {canDelete && (
        <div className="runtime-config__actions">
          <button
            type="button"
            className="feature-table__edit-btn feature-table__edit-btn--danger"
            disabled={isDeleting}
            onClick={onDelete}
            aria-label={`Delete ${entry.name}`}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      )}
    </li>
  );
}
